import React, { useState } from 'react';
import { 
  FaCar, FaTachometerAlt, FaCarSide, FaLeaf, FaBus
} from 'react-icons/fa';
import { 
  SiToyota, SiFord, SiMercedes, SiJeep, SiBmw, SiAudi
} from 'react-icons/si';
import { Link } from 'react-router-dom';

// Import our new centralized data and component
import { allCars } from '../components/carData';
import CarCard from '../components/CarCard';

// Categories (Add all types from your data)
const categories = ["All", "Sedan", "Sport", "2 Door", "SUV", "Van", "Truck", "Coupé"];
const carTypes = ["Sedan", "Sport", "SUV", "Van", "Truck", "Coupé", "Luxury"]; // For the form
const categoryIcons = {
  All: <FaCar />,
  Sedan: <FaTachometerAlt />,
  Sport: <FaCarSide />,
  "2 Door": <FaLeaf />,
  SUV: <FaCar />,
  Van: <FaBus />,
  Truck: <FaCar />,
  Coupé: <FaCarSide />,
};

// --- Filter Buttons Component ---
const VehicleFilters = ({ selectedCategory, onSelectCategory }) => {
  const activeStyle = "bg-black text-white";
  const inactiveStyle = "bg-gray-200 text-gray-700";

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-700">
      <h2 className="text-3xl font-bold text-center mb-8 pt-8 text-white">Select a vehicle group</h2>
      <div className="flex flex-wrap justify-center gap-4">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => onSelectCategory(category)}
            className={`px-6 py-2 rounded-lg font-semibold flex items-center gap-2 transition-colors duration-300 ${
              selectedCategory === category ? activeStyle : inactiveStyle
            }`}
          >
            {categoryIcons[category]} {category}
          </button>
        ))}
      </div>
    </section>
  );
};

// --- Logo Banner Component ---
const LogoBanner = () => {
  const logos = [
    <SiToyota key="toyota" className="text-5xl text-slate-400 min-w-[120px]" />,
    <SiFord key="ford" className="text-5xl text-slate-400 min-w-[120px]" />,
    <SiMercedes key="mercedes" className="text-5xl text-slate-400 min-w-[120px]" />,
    <SiJeep key="jeep" className="text-5xl text-slate-400 min-w-[120px]" />,
    <SiBmw key="bmw" className="text-5xl text-slate-400 min-w-[120px]" />,
    <SiAudi key="audi" className="text-5xl text-slate-400 min-w-[120px]" />,
  ];

  const duplicatedLogos = [...logos, ...logos, ...logos];

  return (
    <section className="bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 relative">
      <div className="logo-slider-container">
        <div className="logo-slider-track">
          {duplicatedLogos.map((logo, index) => (
            <div key={index} className="flex-shrink-0 mx-8">
              {logo}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Main Vehicles Page ---
const Vehicles = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredCars = selectedCategory === "All"
    ? allCars
    : allCars.filter(car => car.type === selectedCategory);

  return (
    // The main background of the page remains dark as requested
    <div className="about-page-bg">
      <VehicleFilters 
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />
      
      {/* Vehicle Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          
          {filteredCars.map((car) => (
            <CarCard 
              key={car.id}
              id={car.id}
              name={car.name} 
              type={car.type} 
              price={car.price} 
              image={car.image} 
              specs={car.specs}
            />
          ))}
          
        </div>
      </section>

      {/* New Request a Car Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto relative rounded-3xl overflow-hidden shadow-2xl transition-all duration-300 ease-in-out hover:scale-[1.01] hover:shadow-[0_0_50px_10px_rgba(251,191,36,0.5)]">
          
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center" 
            style={{ backgroundImage: "url('assets/2021-BMW-M4-Competition.jpg')" }} // Use your desired image path
          ></div>
          
          {/* Dark Overlay for Readability - Adjusted opacity to 70 */}
          <div className="absolute inset-0 bg-slate-800 opacity-70"></div>
          
          {/* Animated Border Effect */}
          <div className="absolute inset-0 ring-4 ring-amber-400/50 rounded-3xl animate-pulse"></div>

          <div className="relative z-10 p-8 md:p-16">
            <h2 className="text-4xl font-bold text-center text-white mb-2">
              Can't find your dream car?
            </h2>
            <p className="text-xl text-center text-slate-400 mb-12">
              Let us know what you are looking for.
            </p>

            <form className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="col-span-1">
                <input 
                  type="text" 
                  placeholder="Full Name" 
                  className="w-full bg-slate-900 border-b-2 border-amber-400 p-4 text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400" 
                />
              </div>
              <div className="col-span-1">
                <input 
                  type="text" 
                  placeholder="Phone Number" 
                  className="w-full bg-slate-900 border-b-2 border-amber-400 p-4 text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400" 
                />
              </div>
              <div className="col-span-1">
                <input 
                  type="email" 
                  placeholder="Email Address" 
                  className="w-full bg-slate-900 border-b-2 border-amber-400 p-4 text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400" 
                />
              </div>

              <div className="col-span-1">
                <select 
                  className="w-full bg-slate-900 border-b-2 border-amber-400 p-4 text-white focus:outline-none focus:ring-1 focus:ring-amber-400"
                >
                  <option value="">Car Type</option>
                  {carTypes.map(type => (
                    <option key={type} value={type} className="bg-slate-900">{type}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-1">
                <input 
                  type="text" 
                  placeholder="Car Company" 
                  className="w-full bg-slate-900 border-b-2 border-amber-400 p-4 text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400" 
                />
              </div>
              <div className="col-span-1">
                <input 
                  type="text" 
                  placeholder="Car Model" 
                  className="w-full bg-slate-900 border-b-2 border-amber-400 p-4 text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400" 
                />
              </div>
              
              <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center mt-6">
                <button
                  type="submit"
                  className="bg-amber-400 text-slate-950 font-bold py-4 px-12 rounded-lg hover:bg-amber-500 transition duration-300"
                >
                  Request a Car
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
      
      <LogoBanner />
    </div>
  );
}

export default Vehicles;